import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ReadonlySectionHeader} from '@github-ui/issue-metadata/ReadonlySectionHeader'
import {Section} from '@github-ui/issue-metadata/Section'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useAlive} from '@github-ui/use-alive'
import {BellIcon, BellSlashIcon} from '@primer/octicons-react'
import {Button, Heading} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {ALIVE_REASONS} from '../../../constants/alive'
import {LABELS} from '../../../constants/labels'
import {commitUpdateIssueSubscriptionMutation} from '../../../mutations/update-issue-subscription'
import type {IssueSecondaryNode} from '../../issue-viewer-secondary-query-types'
import type {IssueViewerViewerFragment} from '../../IssueViewer'
import {fetchSubscriptionStatus, type SubscriptionStatus, subscriptionStatusQueryKey} from './fetch-subscription-status'
import {NotificationSettingsDialog} from './NotificationSettingsDialog'
import styles from './SubscriptionSection.module.css'

// Plain fields derived from the allowlisted `IssueViewerSecondaryViewQuery`. The parent
// (`IssueSidebarLazySections`) still reads this via Relay, so widen with `RelayCompat` to accept
// Relay data. `repository`/`owner` are narrowed to the fields this section selects.
// RELAY-CLEANUP(SubscriptionSection): remove the `RelayCompat` widening once the parent migrates.
export type SubscriptionSectionFragment = RelayCompat<
  Pick<
    IssueSecondaryNode,
    | 'id'
    | 'number'
    | 'threadSubscriptionChannel'
    | 'viewerThreadSubscriptionFormAction'
    | 'viewerCustomSubscriptionEvents'
  > & {
    repository: Pick<IssueSecondaryNode['repository'], 'name'> & {
      owner: Pick<IssueSecondaryNode['repository']['owner'], 'login'>
    }
  }
>

export type SubscriptionSectionProps = {
  issue: SubscriptionSectionFragment
  viewer: IssueViewerViewerFragment | null
}

export function SubscriptionSection({issue, viewer}: SubscriptionSectionProps) {
  const {
    threadSubscriptionChannel,
    id,
    number,
    viewerThreadSubscriptionFormAction,
    viewerCustomSubscriptionEvents,
    repository: {
      name: repo,
      owner: {login: owner},
    },
  } = issue

  // threadSubscriptionChannel should not be null for logged-in users, but check to ensure that it is loaded
  // because useAlive expects a valid channel name.
  return threadSubscriptionChannel ? (
    <SubscriptionSectionInternal
      id={id}
      owner={owner}
      repo={repo}
      number={number}
      threadSubscriptionChannel={threadSubscriptionChannel}
      initialFormAction={viewerThreadSubscriptionFormAction ?? null}
      initialCustomEvents={viewerCustomSubscriptionEvents ?? null}
      viewer={viewer}
    />
  ) : null
}

function SubscriptionSectionInternal({
  id,
  owner,
  repo,
  number,
  threadSubscriptionChannel,
  initialFormAction,
  initialCustomEvents,
  viewer,
}: {
  id: string
  owner: string
  repo: string
  number: number
  threadSubscriptionChannel: string
  initialFormAction: SubscriptionStatus['viewerThreadSubscriptionFormAction']
  initialCustomEvents: SubscriptionStatus['viewerCustomSubscriptionEvents']
  viewer: IssueViewerViewerFragment | null
}) {
  const shouldUpdateAnnouncement = useRef(false)
  const environment = useRelayEnvironment()
  const queryClient = useQueryClient()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const announceSubscriptionUpdates = useCallback((newSubscribed: boolean, success = true) => {
    if (success) {
      const message = newSubscribed
        ? LABELS.notifications.subscribedAnnouncement
        : LABELS.notifications.unsubscribedAnnouncement
      announce(message)
    } else {
      announce(LABELS.somethingWentWrong)
    }
  }, [])

  // we seed this from the secondary query's plain props (`initialData`), so there is no fetch on mount.
  // `staleTime: Infinity` keeps the seed authoritative until we explicitly refetch via
  // `invalidateQueries` after a subscribe/unsubscribe or an Alive channel event.
  const {data} = useQuery({
    queryKey: subscriptionStatusQueryKey(owner, repo, number),
    queryFn: ({signal}) => fetchSubscriptionStatus(owner, repo, number, signal),
    initialData: {
      id,
      viewerThreadSubscriptionFormAction: initialFormAction,
      viewerCustomSubscriptionEvents: initialCustomEvents,
    },
    // so it wont auto-refetch on mount/focus/remount (instead only by the explicit "invalidateQueries" which happens after a toggle)
    // not doing initialDataUpdatedAt bc we dont rely on the time-based staleness
    staleTime: Infinity,
  })
  const {viewerThreadSubscriptionFormAction, viewerCustomSubscriptionEvents, id: issueId} = data
  const subscribed = viewerThreadSubscriptionFormAction === 'UNSUBSCRIBE'
  // Spreading this because it's 'readonly' and cannot be assigned to the mutable type 'ThreadSubscriptionEvent[]' when passing it to the Dialog
  const preSelectedCustomOptions = viewerCustomSubscriptionEvents ? [...viewerCustomSubscriptionEvents] : []

  const refreshSubscription = useCallback(() => {
    shouldUpdateAnnouncement.current = true
    void queryClient.invalidateQueries({queryKey: subscriptionStatusQueryKey(owner, repo, number)})
  }, [queryClient, owner, repo, number])

  useEffect(() => {
    if (shouldUpdateAnnouncement.current) {
      announceSubscriptionUpdates(subscribed)
      shouldUpdateAnnouncement.current = false
    }
  }, [subscribed, announceSubscriptionUpdates])

  const toggleSubscription = useCallback(() => {
    const newSubscribed = !subscribed
    const newSubscriptionState = newSubscribed ? 'SUBSCRIBED' : 'UNSUBSCRIBED'
    const newFormAction: SubscriptionStatus['viewerThreadSubscriptionFormAction'] = newSubscribed
      ? 'UNSUBSCRIBE'
      : 'SUBSCRIBE'
    sendEvent('notifications.thread_subscription.toggle', {
      subscriptionState: newSubscriptionState,
      threadClass: 'Issue',
      threadId: id,
    })

    // as the subscription button no longer reads from the relay store, we need the optimistic update in the cache.
    // a plain subscribe/unsubscribe clears custom events server-side, so we need to reset viewerCustomSubscriptionEvents too. otherwise re-subscribing would leave stale custom events that wrongly preselect the "Custom" option in the dialog
    const queryKey = subscriptionStatusQueryKey(owner, repo, number)
    const previousData = queryClient.getQueryData<SubscriptionStatus>(queryKey)
    queryClient.setQueryData<SubscriptionStatus>(queryKey, prev =>
      prev ? {...prev, viewerThreadSubscriptionFormAction: newFormAction, viewerCustomSubscriptionEvents: null} : prev,
    )

    commitUpdateIssueSubscriptionMutation({
      environment,
      input: {
        subscribableId: id,
        state: newSubscriptionState,
      },
      onCompleted: () => {
        announceSubscriptionUpdates(newSubscribed)
      },
      onError: () => {
        queryClient.setQueryData(queryKey, previousData)
        announceSubscriptionUpdates(subscribed, false)
      },
    })
  }, [environment, id, owner, repo, number, subscribed, queryClient, announceSubscriptionUpdates])

  // Thread subscription updates are scoped to an individual user and emitted via signed user channels.
  useAlive(threadSubscriptionChannel, (event: {reason: string}) => {
    if (!event?.reason) return
    // Refetch subscription information if we receive a message that the subscription has changed.
    // We cannot rely on the "reason" to determine the new state, because we still receive a subscription
    // message if the thread is "ignored".
    if (event.reason === ALIVE_REASONS.SUBSCRIBED && !subscribed) {
      refreshSubscription()
    } else if (event.reason === ALIVE_REASONS.UNSUBSCRIBED && subscribed) {
      refreshSubscription()
    }
  })

  const buttonRef = useRef<HTMLButtonElement>(null)

  const sectionHeader = (
    <div className={clsx(styles.container)}>
      <Heading id={id} as="h3" className={clsx(styles.header)}>
        Notifications
      </Heading>
      <button onClick={() => setIsDialogOpen(true)} className={clsx(styles.button)} ref={buttonRef}>
        {LABELS.notifications.customizeNotificationsSettings}
      </button>
      {isDialogOpen && (
        <NotificationSettingsDialog
          title={LABELS.notifications.notificationsSettings}
          onClose={() => setIsDialogOpen(false)}
          returnFocusRef={buttonRef}
          threadId={issueId}
          preselectedViewerSubscriptionEvents={preSelectedCustomOptions}
          subscribed={subscribed}
          onSaved={() =>
            void queryClient.invalidateQueries({queryKey: subscriptionStatusQueryKey(owner, repo, number)})
          }
        />
      )}
    </div>
  )

  // It controls whether email and web notifications for issues should be sent
  // via notifyd, and whether notifyd should be the primary source of truth
  // for issues related subscriptions and settings.
  const isNotifydEnabledIssuesWatchActivity = isFeatureEnabled('notifyd_issue_watch_activity_notify')

  // Controls whether the settings for sending notifications for
  // participating activity should be written to notify
  const isNotifydEnabledIssuesThreadSubscription = isFeatureEnabled('notifyd_enable_issue_thread_subscriptions')

  const isCustomizeThreadSubscriptionDisabled = isFeatureEnabled('disable_customize_option_thread_subscriptions')

  // Both of the above flags are used in the backend logic of when to show the customise option: https://github.com/github/github/blob/66d24898297b3a2eba7133165ae6c2ab72c3f64a/packages/issues/app/models/issue/notifyd_adapter.rb#L26%7CIssue::NotifydAdapter
  // If they are both enabled, it means that the customisation option should not be shown
  const customizeDisabled = isCustomizeThreadSubscriptionDisabled
    ? true
    : viewer?.isEnterpriseManagedUser || isNotifydEnabledIssuesThreadSubscription || isNotifydEnabledIssuesWatchActivity

  return (
    <Section
      sectionHeader={
        customizeDisabled ? <ReadonlySectionHeader title={LABELS.notifications.notificationsTitle} /> : sectionHeader
      }
    >
      <div className="width-full d-flex flex-column flex-items-stretch pt-2 px-2">
        <Button
          aria-describedby="issue-viewer-subscription-description"
          leadingVisual={subscribed ? BellSlashIcon : BellIcon}
          onClick={toggleSubscription}
          size="small"
        >
          {subscribed ? LABELS.notifications.subscribedButton : LABELS.notifications.unsubscribedButton}
        </Button>
        <p id="issue-viewer-subscription-description" className="my-2 f6 fgColor-muted">
          {subscribed ? LABELS.notifications.subscribedDescription : LABELS.notifications.unsubscribedDescription}
        </p>
      </div>
    </Section>
  )
}

export function SubscriptionSectionFallback() {
  return (
    <Section sectionHeader={<ReadonlySectionHeader title="Notifications" />}>
      <div className="width-full d-flex flex-column flex-items-stretch pt-2 px-2">
        <Button aria-describedby="issue-viewer-subscription-description" leadingVisual={BellIcon}>
          {LABELS.notifications.unsubscribedButton}
        </Button>
        <p id="issue-viewer-subscription-description" className="my-2 f6 fgColor-muted">
          {LABELS.notifications.unsubscribedDescription}
        </p>
      </div>
    </Section>
  )
}
